import { Router } from "@frui.ts/screens/dist";
import type { EntityNames } from "config";
import { Modules } from "config";
import subDays from "date-fns/subDays";
import type ExemptionReason from "entities/exemptionReason";
import ExemptionResponse from "entities/exemptionResponse";
import LicensePlate from "entities/licensePlate";
import cloneDeep from "lodash/cloneDeep";
import { action, computed, toJS } from "mobx";
import ExemptionRepository from "repositories/exemptionRepository";
import EnumService from "services/enumService";
import EventBus, {
  callCenterViewModelName,
  exemptionTerminated,
  totalCountCharges,
  totalCountExemptions,
  totalCountMessages,
  totalCountVehicleRegister,
  vehicleSearchStatus,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import OptionsService from "services/optionsService";
import type CountrySelectViewModel from "../../../countrySelectViewModel";
import CallCenterListViewModel from "../callCenterListViewModel";
import type ExemptionsRequest from "entities/exemptionsRequest";
import type ExemptionRegistrationMethod from "entities/exemptionRegistrationMethod";
import ChargesRepository from "repositories/chargesRepository";
import { isAfter, isBefore } from "date-fns";
import type ExemptionDetailSectionViewModel from "./detailExemption/exemptionDetailSectionViewModel";
import { bound } from "@frui.ts/helpers";
import EmailMessageRepository from "repositories/emailMessageRepository";
import type CreateExemptionViewModel from "./createExemption/createExemptionViewModel";
import type ExemptionChangeRequest from "entities/exemptionChangeRequest";
import type EditExemptionViewModel from "./editExemption/editExemptionViewModel";
import type TerminateExemptionViewModel from "./terminateExemption/terminateExemptionViewModel";
import type DeleteExemptionViewModel from "./deleteExemption/deleteExemptionViewModel";
import { emptyValue } from "utils/emptyValue";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";

export const exemptionsNavigationName = CallCenterModelNamesEnum.Exemptions;

export enum ExemptionAction {
  ExemptionDetailAccess = "exemptionDetailAccess",
  ExemptionChange = "exemptionChange",
  ExemptionCancellation = "exemptionCancellation",
  ExemptionDeletion = "exemptionDeletion",
}

export enum ExemptionsRowColor {
  WHITE = "row-white",
  GRAY = "row-darkgray",
  GREEN = "row-green",
}
@Router.registerRoute({ route: exemptionsNavigationName, name: Router.Self })
export default class ExemptionsViewModel extends CallCenterListViewModel<ExemptionResponse, ExemptionsRequest> {
  protected readonly modelName: EntityNames = "exemption";
  countrySelectViewModel: CountrySelectViewModel;

  constructor(
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected enumService: EnumService,
    protected securityService: SecurityService,
    protected repository: ExemptionRepository,
    protected chargesRepository: ChargesRepository,
    protected vehicleRegisterRepository: VehicleRegisterRepository,
    protected emailMessageRepository: EmailMessageRepository,
    private optionsService: OptionsService,
    private exemptionDetailSectionVMFactory: ReturnType<typeof ExemptionDetailSectionViewModel.Factory>,
    private exemptionCreateVMFactory: ReturnType<typeof CreateExemptionViewModel.Factory>,
    private exemptionEditViewModel: ReturnType<typeof EditExemptionViewModel.Factory>,
    private terminateExemptionVMFactory: ReturnType<typeof TerminateExemptionViewModel.Factory>,
    private deleteExemptionVMFactory: ReturnType<typeof DeleteExemptionViewModel.Factory>,
    private countrySelectVMFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = exemptionsNavigationName;
    this.countrySelectViewModel = this.countrySelectVMFactory("id");
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(this.eventBus.subscribe(exemptionTerminated, this.onExemptionTerminated));
    return super.onInitialize();
  }

  protected onActivate() {
    this.eventBus.publish(callCenterViewModelName({ callCenterViewModelName: CallCenterModelNamesEnum.Exemptions }));

    return super.onActivate();
  }

  @computed
  get licensePlate(): LicensePlate {
    return {
      licensePlate: this.appliedFilter.licensePlate ?? "",
      countryId: this.appliedFilter.countryId ?? "",
    };
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "exemptions.name");
  }

  @computed
  get areAllExemptionTerminated() {
    return this.items.reduce((acc, exemption) => acc && !!exemption.validUntil, true);
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("exemption_reasons");
  }

  @computed
  get actions() {
    return Object.values(ExemptionAction);
  }

  @computed
  get isEditable() {
    return this.securityService.allowTo("callcenter", "write");
  }

  @computed
  get allowVehicleRehisterSearch() {
    return this.securityService.allowTo("callcenter");
  }

  @bound
  rowColor(validSince: Date, validUntil?: Date): string {
    const validUntilBool = !validUntil || isBefore(new Date(), validUntil);
    const isValid = (isAfter(new Date(), validSince) && validUntilBool) || (isBefore(new Date(), validSince) && validUntilBool);

    return isValid ? ExemptionsRowColor.GREEN : ExemptionsRowColor.GRAY;
  }

  @bound
  getExemptionReason(exemptionReasonId: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");

    return exemptionReasons.find(x => x.id === exemptionReasonId);
  }

  @action.bound
  protected async loadItems() {
    const { countryId, licensePlate, officialRecordId } = this.appliedFilter;
    const result = await this.repository.getAll(
      { countryId, licensePlate, officialRecordId },
      this.pagingFilter,
      this.abortController?.signal
    );
    this.publishTotalCount(result.metadata.total);

    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @action.bound
  protected async loadRelatedCounts() {
    const { countryId, licensePlate, emailAddress, orderIdent } = this.appliedFilter;

    if (emailAddress || licensePlate || orderIdent || (countryId && licensePlate)) {
      const { totalCount } = await this.emailMessageRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountMessages({ totalCountMessages: totalCount }));
    } else {
      this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
    }

    const { totalCount } = await this.chargesRepository.totalCount(this.appliedFilter);
    this.eventBus.publish(totalCountCharges({ totalCountCharges: totalCount ?? 0 }));

    if (this.allowVehicleRehisterSearch && (licensePlate || (countryId && licensePlate))) {
      const { totalCount, searchStatus } = await this.vehicleRegisterRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: totalCount }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: searchStatus }));
    } else {
      this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
      this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
    }
  }

  @action.bound
  protected publishTotalCount(totalCount: number) {
    this.eventBus.publish(totalCountExemptions({ totalCountExemptions: totalCount }));
  }

  @action.bound
  protected resetRelatedCounts() {
    this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
    this.eventBus.publish(totalCountCharges({ totalCountCharges: 0 }));
    this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: 0 }));
    this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
  }

  @action.bound
  permittedActions(item: ExemptionResponse, action: ExemptionAction) {
    const permittedAction = item.permittedActions[action];
    if (
      !this.isEditable &&
      (action === ExemptionAction.ExemptionCancellation ||
        action === ExemptionAction.ExemptionChange ||
        action === ExemptionAction.ExemptionDeletion)
    ) {
      return {
        ...permittedAction,
        isAllowed: false,
      };
    } else {
      return permittedAction;
    }
  }

  @action.bound
  handleDropdownAction(item: ExemptionResponse, action: ExemptionAction) {
    switch (action) {
      case ExemptionAction.ExemptionDetailAccess:
        return this.onExemptionDetail(item);
      case ExemptionAction.ExemptionCancellation:
        return this.onTerminateExemptionClick(item);
      case ExemptionAction.ExemptionChange:
        return this.onItemEditClick(item);
      case ExemptionAction.ExemptionDeletion:
        return this.onDeleteItemClick(item);
    }
  }

  @action.bound
  onTerminateExemptionClick(item: ExemptionResponse) {
    const dialog = this.terminateExemptionVMFactory(item);
    this.openItem = item;
    this.tryActivateChild(dialog);
  }

  @action.bound
  onAddExemptionClick() {
    this.tryActivateChild(this.exemptionCreateVMFactory(this.licensePlate));
  }

  @action.bound
  onExemptionDetail(item: ExemptionResponse) {
    this.tryActivateChild(this.exemptionDetailSectionVMFactory(item));
  }

  @action.bound
  onItemEditClick(item: ExemptionResponse) {
    const itemForEditation = cloneDeep(toJS(item)) as any as ExemptionChangeRequest;

    this.tryActivateChild(this.exemptionEditViewModel(itemForEditation));
  }

  @action.bound
  onDeleteItemClick(item: ExemptionResponse) {
    this.tryActivateChild(this.deleteExemptionVMFactory(item));
  }

  @action.bound
  private onExemptionTerminated(event: ReturnType<typeof exemptionTerminated>) {
    const terminatedExemptionIndex = this.items.findIndex(
      exemption => exemption.exemptionId === event.payload.entity.exemptionId
    );

    if (~terminatedExemptionIndex) {
      if (
        !!this.appliedFilter.licensePlate &&
        event.payload.entity.validUntil &&
        event.payload.entity.validUntil < subDays(new Date(), 14)
      ) {
        this.items.splice(terminatedExemptionIndex, 1);
      } else {
        this.items[terminatedExemptionIndex] = this.enumService.proxyObjectEnums(event.payload.entity);
      }
    }
  }

  translateSubAttribute(model: "licensePlate", attribute: keyof LicensePlate): string;
  translateSubAttribute(model: "exemptionReason", attribute: ExemptionReason): string;
  translateSubAttribute(model: "exemptionReason" | "licensePlate", attribute: keyof LicensePlate | ExemptionReason) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateExemptionReasonId(exemptionReasonId: string) {
    const matchingExemptionReason = this.getExemptionReason(exemptionReasonId);
    return matchingExemptionReason?.description ?? emptyValue;
  }

  translateExemptionRegistrationMethod(exemptionRegistrationMethod?: ExemptionRegistrationMethod) {
    if (!exemptionRegistrationMethod) {
      return emptyValue;
    }
    return this.localizationService.translateEnum("exemptionRegistrationMethod", exemptionRegistrationMethod);
  }
}
